import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Favorites (wishlist) feature.
 *
 * - {@link FavoritesStore} persists a snapshot of each favorited product in the
 *   browser and broadcasts changes via the `favorites:update` event.
 * - `<favorite-toggle>` is the heart button shown on product cards.
 * - `<favorites-drawer-component>` is the header popup that lists favorites and
 *   lets the shopper quick-add them to the cart.
 *
 * Storage is intentionally client-side so it works for guests and logged-in
 * customers alike. Swap `STORAGE` to `window.sessionStorage` if favorites
 * should only live for the current browser session.
 */

/** @typedef {Window['localStorage']} WebStorage */
/** @type {WebStorage} */
const STORAGE = window.localStorage;

const STORAGE_KEY = 'theme:favorites';

/** Event dispatched on `document` whenever the favorites list changes. */
export const FAVORITES_UPDATE = 'favorites:update';

/**
 * A snapshot of a product, captured when it is favorited so the drawer can be
 * rendered without any network request.
 *
 * @typedef {object} FavoriteItem
 * @property {string} id - The product id.
 * @property {string} [handle] - The product handle.
 * @property {string} [title] - The product title.
 * @property {string} [url] - The product URL.
 * @property {string} [image] - A featured image URL.
 * @property {string} [price] - The pre-formatted price (money output).
 * @property {boolean} [available] - Whether the product can be purchased.
 * @property {string} [variantId] - The variant id used for quick add to cart.
 * @property {number} [rating] - The average rating (0 when none).
 * @property {number} [ratingCount] - The number of reviews.
 */

/**
 * Reads, writes and broadcasts the favorites list.
 */
export class FavoritesStore {
  /** @returns {FavoriteItem[]} */
  static getAll() {
    try {
      const raw = STORAGE.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {string | number} id
   * @returns {boolean}
   */
  static has(id) {
    const target = String(id);
    return FavoritesStore.getAll().some((item) => item.id === target);
  }

  /**
   * Persists the list and notifies listeners.
   * @param {FavoriteItem[]} items
   */
  static #save(items) {
    try {
      STORAGE.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Unable to persist favorites', error);
    }
    document.dispatchEvent(new CustomEvent(FAVORITES_UPDATE, { detail: { items } }));
  }

  /**
   * Adds an item (newest first). No-op if already present.
   * @param {FavoriteItem} item
   * @returns {FavoriteItem[]}
   */
  static add(item) {
    const items = FavoritesStore.getAll();
    if (items.some((existing) => existing.id === item.id)) return items;

    const next = [item, ...items];
    FavoritesStore.#save(next);
    return next;
  }

  /**
   * Removes an item by product id.
   * @param {string | number} id
   * @returns {FavoriteItem[]}
   */
  static remove(id) {
    const target = String(id);
    const next = FavoritesStore.getAll().filter((item) => item.id !== target);
    FavoritesStore.#save(next);
    return next;
  }

  /**
   * Adds the item when missing, removes it when present.
   * @param {FavoriteItem} item
   * @returns {boolean} The new favorited state.
   */
  static toggle(item) {
    if (FavoritesStore.has(item.id)) {
      FavoritesStore.remove(item.id);
      return false;
    }
    FavoritesStore.add(item);
    return true;
  }
}

/**
 * Builds a {@link FavoriteItem} from a `data-*` dataset.
 * @param {DOMStringMap} data
 * @returns {FavoriteItem}
 */
function itemFromDataset(data) {
  return {
    id: String(data.productId),
    handle: data.productHandle,
    title: data.productTitle,
    url: data.productUrl,
    image: data.productImage || '',
    price: data.productPrice || '',
    available: data.productAvailable === 'true',
    variantId: data.variantId || '',
    rating: Number.parseFloat(data.rating || '0') || 0,
    ratingCount: Number.parseInt(data.ratingCount || '0', 10) || 0,
  };
}

/**
 * The heart toggle rendered on product cards (and reusable elsewhere).
 *
 * @typedef {object} FavoriteToggleRefs
 * @property {HTMLButtonElement} button - The toggle button.
 *
 * @extends {Component<FavoriteToggleRefs>}
 */
class FavoriteToggle extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(FAVORITES_UPDATE, this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FAVORITES_UPDATE, this.#sync);
  }

  /** @returns {string} */
  get productId() {
    return String(this.dataset.productId);
  }

  /**
   * Toggles this product's favorite state.
   * @param {Event} event
   */
  toggle = (event) => {
    // Product cards are clickable; keep the heart from navigating to the PDP.
    event.preventDefault();
    event.stopPropagation();
    FavoritesStore.toggle(itemFromDataset(this.dataset));
  };

  /** Reflects the current favorited state in the DOM. */
  #sync = () => {
    const active = FavoritesStore.has(this.productId);
    this.classList.toggle('is-favorite', active);
    this.refs.button.setAttribute('aria-pressed', String(active));

    const { labelAdd, labelRemove } = this.dataset;
    if (labelAdd && labelRemove) {
      this.refs.button.setAttribute('aria-label', active ? labelRemove : labelAdd);
    }
  };
}

if (!customElements.get('favorite-toggle')) {
  customElements.define('favorite-toggle', FavoriteToggle);
}

/**
 * The header favorites popup. Lists favorited products and supports removing
 * them or quick-adding them to the cart.
 *
 * @typedef {object} FavoritesDrawerRefs
 * @property {HTMLDialogElement} dialog - The drawer dialog.
 * @property {HTMLUListElement} list - The list that holds favorite items.
 * @property {HTMLElement} emptyState - The "no favorites" message.
 * @property {HTMLTemplateElement} itemTemplate - The template for a single favorite.
 * @property {HTMLElement} count - The count bubble in the header trigger.
 *
 * @extends {DialogComponent}
 */
class FavoritesDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'list', 'emptyState', 'itemTemplate', 'count'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(FAVORITES_UPDATE, this.#render);
    this.addEventListener(DialogOpenEvent.eventName, this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FAVORITES_UPDATE, this.#render);
    this.removeEventListener(DialogOpenEvent.eventName, this.#render);
  }

  /** Opens the drawer (used by the header trigger). */
  open() {
    this.showDialog();
  }

  /** Closes the drawer (used by the close button). */
  close() {
    this.closeDialog();
  }

  /**
   * Removes a favorite. The list re-renders via the `favorites:update` event.
   * @param {Event} event
   */
  removeItem(event) {
    const row = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-favorite-id]') : null
    );
    if (!row?.dataset.favoriteId) return;
    FavoritesStore.remove(row.dataset.favoriteId);
  }

  /**
   * Quick-adds a favorite to the cart.
   * @param {Event} event
   */
  addToCart = async (event) => {
    const row = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-favorite-id]') : null
    );
    const variantId = row?.dataset.variantId;
    if (!row || !variantId) return;

    const button = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-field="add"]'));
    if (button?.disabled) return;
    if (button) button.disabled = true;

    try {
      const sections = [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
        .filter(Boolean)
        .join(',');

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections }),
      });
      const data = await response.json();

      if (data.status) {
        this.#announce(data.description || data.message || '');
        return;
      }

      // Re-read the cart so listeners (cart icon, cart drawer) get the true totals.
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'favorites',
          itemCount: cart.item_count,
          productId: row.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      // The product is now in the cart, so move it out of favorites. Only this
      // item is removed; the rest of the list is preserved (the re-render is
      // driven by the `favorites:update` event, not a section morph).
      FavoritesStore.remove(row.dataset.favoriteId);
    } catch (error) {
      console.error('Favorites: failed to add to cart', error);
    } finally {
      if (button) button.disabled = false;
    }
  };

  /**
   * Announces a message to assistive tech via the dialog's live region.
   * @param {string} message
   */
  #announce(message) {
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /** Renders the favorites list and updates the count bubble. */
  #render = () => {
    const items = FavoritesStore.getAll();

    this.refs.count.textContent = items.length ? String(items.length) : '';
    this.toggleAttribute('data-has-favorites', items.length > 0);

    const fragment = document.createDocumentFragment();
    for (const item of items) {
      fragment.appendChild(this.#renderItem(item));
    }
    this.refs.list.replaceChildren(fragment);

    this.refs.emptyState.hidden = items.length > 0;
    this.refs.list.hidden = items.length === 0;
  };

  /**
   * Builds a single favorite row from the template.
   * @param {FavoriteItem} item
   * @returns {HTMLElement}
   */
  #renderItem(item) {
    const root = this.refs.itemTemplate.content.firstElementChild;
    const node = /** @type {HTMLElement} */ (root.cloneNode(true));

    node.dataset.favoriteId = item.id;
    node.dataset.productId = item.id;
    node.dataset.variantId = item.variantId || '';

    const links = node.querySelectorAll('[data-field="link"]');
    for (const link of links) {
      if (link instanceof HTMLAnchorElement && item.url) link.href = item.url;
    }

    const image = node.querySelector('[data-field="image"]');
    if (image instanceof HTMLImageElement) {
      if (item.image) {
        image.src = item.image;
        image.alt = item.title || '';
      } else {
        image.remove();
      }
    }

    const title = node.querySelector('[data-field="title"]');
    if (title) title.textContent = item.title || '';

    const price = node.querySelector('[data-field="price"]');
    // Strip any markup the money filter may have wrapped the value in (some
    // shops set a money format like `<span class="money">$10</span>`). Also
    // cleans values stored before this fix.
    if (price) price.textContent = (item.price || '').replace(/<[^>]*>/g, '');

    const rating = node.querySelector('[data-field="rating"]');
    if (rating) {
      if (item.rating && item.rating > 0) {
        rating.appendChild(this.#buildStars(item.rating, item.ratingCount || 0));
      } else {
        rating.remove();
      }
    }

    const add = /** @type {HTMLButtonElement | null} */ (node.querySelector('[data-field="add"]'));
    if (add && (!item.variantId || item.available === false)) {
      add.disabled = true;
      add.dataset.soldOut = 'true';
    }

    return node;
  }

  /**
   * Recreates the theme's star rating markup, reusing the shared `#favorite-star`
   * symbol and `#favorite-star-half` gradient rendered in the drawer.
   * Mirrors the `rating-stars` snippet's half/full rounding.
   *
   * @param {number} rating
   * @param {number} count
   * @returns {HTMLElement}
   */
  #buildStars(rating, count) {
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const XLINK_NS = 'http://www.w3.org/1999/xlink';

    const wrapper = document.createElement('div');
    wrapper.className = 'rating-wrapper rating-color--primary favorites-drawer__rating';

    const rng = document.createElement('div');
    rng.className = 'rating';
    rng.style.setProperty('--star-size', '1.1em');
    rng.setAttribute('aria-label', this.#ratingLabel(rating));

    const decimal = rating % 1;
    let decimalRating = 0;
    if (decimal >= 0.3 && decimal <= 0.7) decimalRating = 0.5;
    else if (decimal > 0.7) decimalRating = 1;
    const filled = Math.floor(rating) + Math.ceil(decimalRating);

    for (let star = 1; star <= 5; star++) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 32 32');
      svg.setAttribute('role', 'presentation');
      svg.classList.add('stars');
      if (star <= filled) svg.classList.add('filled-star');
      svg.style.setProperty('--empty-star-fill-color', 'rgb(var(--star-fill-color-rgb) / var(--opacity-20))');

      const use = document.createElementNS(SVG_NS, 'use');
      use.setAttributeNS(XLINK_NS, 'xlink:href', '#favorite-star');
      use.setAttribute('href', '#favorite-star');
      if (decimalRating === 0.5 && star === filled) use.setAttribute('fill', 'url(#favorite-star-half)');

      svg.appendChild(use);
      rng.appendChild(svg);
    }
    wrapper.appendChild(rng);

    if (count > 0) {
      const label = document.createElement('p');
      label.className = 'rating-count paragraph';
      label.textContent = `${count} ${this.dataset.reviewsLabel || 'reviews'}`.trim();
      wrapper.appendChild(label);
    }

    return wrapper;
  }

  /**
   * @param {number} rating
   * @returns {string}
   */
  #ratingLabel(rating) {
    const template = this.dataset.ratingLabel || 'Rating of this product is %rating% out of 5';
    return template.replace('%rating%', String(rating));
  }
}

if (!customElements.get('favorites-drawer-component')) {
  customElements.define('favorites-drawer-component', FavoritesDrawerComponent);
}
